import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { teamApi } from '../../api/teams';
import type { TeamDashboard } from '../../api/collaboration';

export function TeamDashboardPage() {
  const { id = '' } = useParams();
  const [dashboard, setDashboard] = useState<TeamDashboard | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    void teamApi
      .dashboard(id)
      .then(setDashboard)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p className="p-6 text-slate-600">Loading dashboard…</p>;
  if (error || !dashboard) return <p className="p-6 text-red-600">{error || 'Dashboard unavailable'}</p>;

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to={`/teams/${id}`} className="text-sm text-slate-600 hover:underline">← Team</Link>
      <h1 className="mt-4 text-2xl font-semibold">{dashboard.team.name} Dashboard</h1>
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border bg-white p-4">
          <p className="text-sm text-slate-500">Members</p>
          <p className="mt-1 text-2xl font-semibold">{dashboard.member_count}</p>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <p className="text-sm text-slate-500">Sub-teams</p>
          <p className="mt-1 text-2xl font-semibold">{dashboard.sub_team_count}</p>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <p className="text-sm text-slate-500">Updated</p>
          <p className="mt-1 text-sm font-medium">{new Date(dashboard.generated_at).toLocaleString()}</p>
        </div>
      </div>
      <section className="mt-8 rounded-xl border bg-white p-6">
        <h2 className="font-medium">Recent activity</h2>
        {dashboard.recent_activity?.length ? (
          <ul className="mt-4 space-y-2 text-sm">
            {dashboard.recent_activity.map((a, i) => (
              <li key={i} className="rounded border border-slate-100 px-3 py-2">
                <span className="font-medium">{a.action}</span>
                {a.summary && <span className="text-slate-500"> — {a.summary}</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-slate-600">No recent team activity.</p>
        )}
      </section>
    </div>
  );
}
